import classNames, { Argument } from "classnames/dedupe";
import React, {
  CSSProperties,
  ElementType,
  ReactNode,
  useContext,
} from "react";
import LinkContext from "./LinkContext";

export interface ILink<T = HTMLLinkElement> {
  /** 'href' attribute for inner 'a' element */
  href?: string;
  /** Inner 'text' label for a link */
  text?: ReactNode;
  /** Pass any params that can occur in your */
  linkParams?: Partial<T>;
  /** A callback function for when this component is clicked */
  onClick?(e?: MouseEvent): void;
}
export interface ILinkProps<LinkType = HTMLLinkElement>
  extends ILink<LinkType> {
  /** Element to draw link as (for example, "a" tag).
   *  @default "a"
   *  */
  as?: ElementType;
  /**
   * Set Link variant, decorates component with css classes
   * @type TLinkVariant
   */
  variant?: TLinkVariant;

  /** Custom `title` property value */
  title?: ReactNode;

  /** Indicates that this link is current route */
  active?: boolean;

  target?: string;

  rel?: string;

  /**
   * The custom set of css classes used to style component
   *
   */
  classes?: {
    /** Styles applied to outer 'Link' ('a' by default) element. */
    root?: Argument;
  };

  style?: CSSProperties;

  /**
   * Children nodes of current Link component.
   * !!! ONLY ONE ELEMENT FOR COMPATIBILITY WITH NEXT.JS` NEXT/LINK COMPONENT !!!
   */
  children?: JSX.Element | string;
  /**
   * Disables all css changes on hover and active states
   */
  disabled?: boolean;
}

export type TLinkVariant = "default" | "inline";

enum ClassMapping {
  default = "",
  inline = "inline-link",
}

function Link<LinkType>({
  as: Tag,
  href,
  text,
  variant = "default",
  classes,
  target,
  title,
  linkParams,
  children,
  onClick,
  ...rest
}: ILinkProps<LinkType>): JSX.Element {
  const rootClasses = classNames(ClassMapping[variant], classes?.root);

  const { wrapper: LinkCustomContextWrapper } = useContext(LinkContext);

  const RenderComponent = Tag || LinkCustomContextWrapper || "a";

  return (
    <RenderComponent
      href={href}
      target={target}
      className={rootClasses}
      onClick={onClick}
      data-testid="link-component"
      title={title}
      {...(linkParams || {})}
      {...rest}
    >
      {children}
      {text}
    </RenderComponent>
  );
}

export default Link;
